// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { StyledOverlayComponent } from "@synd/App/Components/Common/StyledOverlay";
import { menuBodyStyle } from "@synd/App/Components/Menu/DropdownMenu";
import { xBaseTheme } from "@synd/XDL/types";
import { color, getSystemColor, xScreen } from "@synd/XDL/Utilities";
import { css } from "styled-components";

export const zIndexLoadingOverlay = 1500;

export const hideLoadingOverlay = css`
    #react-app:not(.allow-loadingOverlay) {
        svg[class^="LoadingSpinnerStyle"],
        div[class^="LoadingOverlayStyle"] {
            visibility: hidden;
        }
    }
`;

export const xngLoadingOverlayUtilitiesStyles = css`
    #xng-loading-overlay {
        width: 100%;
        position: fixed;
        height: 100%;
        z-index: ${zIndexLoadingOverlay};
        top: 0;
        bottom: 0;
        @media print {
            display: none;
        }
    }
`;

export default {
    css: css`
        ${xngLoadingOverlayUtilitiesStyles}
        ${hideLoadingOverlay}
        .enlarged-img {
            z-index: 1;
            background: ${getSystemColor("common.white")};
        }
        html {
            scroll-behavior: smooth;
        }
        body {
            &:after {
                transition: all 0.2s linear;
                content: "";
                position: fixed;
                top: 0;
                left: 0;
                z-index: ${getProp("theme.zIndex.shade")};
                width: 100%;
                height: 100%;
                pointer-events: none;
            }
            &.shade {
                &:after {
                    background-color: ${color("common.ui7", 0.7)};
                }
            }

            &.drawer-open ${StyledOverlayComponent} {
                z-index: 1289;
            }
        }

        .button {
            background-color: ${color("common.ui4")};
            border: 2px solid ${color("common.ui4")};
            color: ${color("common.white")};
            display: inline-flex;
            padding: 0 24px;
            height: 33px;
            font-size: 12px;
            border-radius: 2px;

            && span {
                color: ${color("common.white")};
            }

            &&:not(:disabled):hover {
                background-color: ${color("common.ui3")};
                color: ${color("common.white")};
                border: 2px solid ${color("common.border")};

                && span {
                    color: ${color("common.white")};
                }
            }

            &&:not(:disabled):focus {
                background-color: ${color("common.ui5")};
                border: 2px solid ${color("common.ui5")};
            }
            &&,
            &&:disabled {
                border: 2px solid ${color("common.ui4")};
                opacity: 0.33;
            }
        }

        // This style to fix an issue with Google Places autocomplete
        .pac-container {
            z-index: 10000 !important;
        }

        .date-picker-wrapper {
            display: flex;
        }
        b,
        strong {
            font-weight: 600;
        }

        ${menuBodyStyle}

        .hide-on-mobile {
            ${xScreen("lte", "sm")} {
                display: none;
            }
        }
        .hide-on-tablet {
            ${xScreen("lte", "md")} {
                display: none;
            }
        }
        .hide-on-print {
            @media print {
                display: none;
            }
        }
        .show-on-print {
            @media screen {
                display: none;
            }
        }

        .swiper-zoom-container {
            display: block;
            width: unset;
            height: unset;
            img {
                cursor: zoom-in;
            }
        }
        .swiper-slide-zoomed {
            img {
                cursor: move;
            }
        }
        .magnifier {
            & > div {
                img {
                    max-height: 60vh;
                }
                > div > img {
                    width: unset;
                    height: unset;
                    max-width: unset !important;
                    max-height: unset;
                }
            }
        }

        .rich-content-link {
            ${TypographyStyle} {
                font-size: inherit;
                font-weight: inherit;
            }
        }
    `,
} as xBaseTheme["globalStyle"];
